import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapProjectDto, mapQueryResult } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { buildSlugFilters, mergeFilters } from '@/ui/composites/grid/gridFilters';
import type { ProjectMetricThresholdUpdate } from '../utils/projectMetricThresholds';
import type { ProjectDto, ProjectSummaryDto, QueryResult } from '@flowqa/shared';

export type ProjectPipelineWebhookTestResult = {
  status: 'ok';
  projectId: string;
  endpoint: string;
  headerName: 'x-webhook-token';
};

export type ProjectApiKeyRotateResult = {
  status: 'rotated';
  projectId: string;
  apiKey: string;
  createdAt: string | Date;
  tags?: string[];
};

export type ProjectWipeDataResult = {
  status: 'wiped' | 'scheduled';
  jobId?: string;
};

export type ProjectWipeDataInput = {
  id: string;
  confirmation: string;
  reasonCode?: string;
  ticket?: string;
  async?: boolean;
};

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryProjects = async (
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<QueryResult<ProjectDto>> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.post<QueryResult<ProjectDto>>(
      'projects/query',
      { page: 1, pageSize: 100, sorts: [{ field: 'name', direction: 'asc' }] },
      {
        token,
        retries: 1,
        // Preserve explicit null to disable implicit org header injection in api client.
        orgId: orgId === undefined ? undefined : orgId,
      },
    );
    return mapQueryResult(result, mapProjectDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryProjectSummaries = async (
  projectIds: string[],
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<ProjectSummaryDto[]> => {
  if (projectIds.length === 0) return [];
  const token = ensureToken(tokenOverride);
  try {
    return await apiClient.post<ProjectSummaryDto[]>(
      'projects/summary',
      { projectIds },
      { retries: 1, orgId: orgId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getProjectBySlug = async (
  slug: string | null,
  orgId?: string,
  tokenOverride?: string | null,
): Promise<ProjectDto | null> => {
  if (!slug) {
    return null;
  }
  const token = ensureToken(tokenOverride);
  try {
    const payload = await apiClient.post<QueryResult<ProjectDto>>(
      'projects/query',
      { page: 1, pageSize: 1, filters: mergeFilters(buildSlugFilters(slug)) },
      { retries: 1, orgId, token },
    );
    const mapped = mapQueryResult(payload, mapProjectDto);
    if (mapped.items.length === 0) return null;
    const first = mapped.items[0];
    return await getProjectById(first.id, orgId, tokenOverride);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getProjectById = async (
  id: string,
  orgId?: string,
  tokenOverride?: string | null,
): Promise<ProjectDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<ProjectDto>(`projects/${id}`, {
      token,
      retries: 1,
      orgId: orgId ?? undefined,
    });
    return mapProjectDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createProject = async (
  input: {
    name: string;
    badgeEnabled?: boolean;
    badgeChars?: ProjectDto['badgeChars'];
    badgeColor?: ProjectDto['badgeColor'];
  },
  tokenOverride?: string | null,
  orgId?: string | null,
): Promise<ProjectDto> => {
  const authToken = ensureToken(tokenOverride);
  const body = {
    name: input.name.trim(),
    badgeEnabled: input.badgeEnabled,
    badgeChars: input.badgeChars?.trim(),
    badgeColor: input.badgeColor,
  };
  try {
    const result = await apiClient.post<ProjectDto>('projects', body, {
      token: authToken,
      retries: 1,
      orgId: orgId ?? undefined,
    });
    return mapProjectDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateProject = async (
  input: {
    id: string;
    name?: string;
    badgeEnabled?: boolean;
    badgeChars?: ProjectDto['badgeChars'];
    badgeColor?: ProjectDto['badgeColor'];
    projectApiKey?: null;
    metricThresholds?: ProjectMetricThresholdUpdate;
  },
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<ProjectDto> => {
  const authToken = ensureToken(tokenOverride);
  const body = {
    name: input.name?.trim(),
    badgeEnabled: input.badgeEnabled,
    badgeChars: input.badgeChars?.trim(),
    badgeColor: input.badgeColor,
    projectApiKey: input.projectApiKey === undefined ? undefined : input.projectApiKey,
    metricThresholds: input.metricThresholds,
  };
  try {
    const result = await apiClient.patch<ProjectDto>(`projects/${input.id}`, body, {
      token: authToken,
      retries: 1,
      orgId: orgId ?? undefined,
    });
    return mapProjectDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteProject = async (
  input: { id: string },
  tokenOverride?: string | null,
): Promise<{ id: string }> => {
  const authToken = ensureToken(tokenOverride);
  try {
    await apiClient.post<undefined>(`projects/${input.id}/delete`, undefined, {
      token: authToken,
      retries: 1,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const testProjectPipelineWebhook = async (
  input: { id: string; apiKey: string },
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<ProjectPipelineWebhookTestResult> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<ProjectPipelineWebhookTestResult>(
      `projects/${input.id}/pipeline-webhook-test`,
      { apiKey: input.apiKey },
      {
        token: authToken,
        retries: 1,
        orgId: orgId ?? undefined,
      },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const rotateProjectApiKey = async (
  input: { id: string; tags?: string[] },
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<ProjectApiKeyRotateResult> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<ProjectApiKeyRotateResult>(
      `projects/${input.id}/api-keys/rotate`,
      { tags: input.tags },
      {
        token: authToken,
        retries: 1,
        orgId: orgId ?? undefined,
      },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const wipeProjectData = async (
  input: ProjectWipeDataInput,
  tokenOverride?: string | null,
): Promise<ProjectWipeDataResult> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<ProjectWipeDataResult>(
      `projects/${input.id}/wipe-data`,
      {
        confirmation: input.confirmation,
        reasonCode: input.reasonCode,
        ticket: input.ticket,
        async: input.async,
      },
      {
        token: authToken,
        retries: 1,
      },
    );
  } catch (err) {
    throw toApiError(err);
  }
};
